import { useEffect, useRef, useState } from 'react';
import { audioContext, unlock } from './audio';

/**
 * The audition.
 *
 * A microphone rises between the two bouncers, the browser asks for the mic,
 * and whatever happens next decides which rejection you get. Nobody passes —
 * the point of the `poziv` branch is that not having a code costs you your
 * dignity and then still sends you back for a code — but the *shape* of the
 * failure is real, and that is the whole joke. Sing and they tell you it was
 * bad. Say nothing and they tell you that was worse.
 *
 * Three outcomes, and each has its own line in vrata.yaml:
 *
 *   sang     something came out of the microphone
 *   silent   the mic worked and heard nothing
 *   denied   the prompt was refused, or there is no microphone at all
 *
 * Nothing is recorded, nothing is uploaded, nothing is even kept: the analyser
 * reads the live graph and the only thing that survives this component is one
 * of those three words. The stream is stopped on every exit path, including
 * the ones nobody plans for — an unmount mid-listen has to drop the browser's
 * recording indicator, or the site looks like it is still listening after the
 * scene has moved on.
 */

export type SingOutcome = 'sang' | 'silent' | 'denied';

const MIC_SRC = '/assets/microphone.png';
export const MIC_W = 139;
export const MIC_H = 250;

/**
 * Frames before this are dropped: a stream that has just opened reports
 * garbage for a few of them, and one burst of garbage is a passing grade.
 */
const WARMUP_MS = 300;
/** How long they get. Long enough to commit to a bit, short enough to sit through. */
const LISTEN_MS = 6000;
/** Cumulative time above threshold that counts as having made a noise. */
const NOISE_MS = 180;

/**
 * The noise floor is a **running minimum over the whole listen**, not an
 * average of the first half-second.
 *
 * Averaging the opening is the obvious design and it is wrong in the one case
 * that matters most: somebody who starts singing the instant the microphone
 * appears calibrates the threshold against their own voice and is then told
 * they said nothing. A running minimum cannot be pushed up by singing — it
 * only ever finds the quietest moment so far, which is what room tone is.
 *
 * The threshold is that floor times a multiple, clamped at both ends:
 *
 *   MIN_RMS  a dead-silent room would otherwise drive the floor to nearly
 *            zero, and four times nearly zero makes a hard-drive tick a
 *            performance.
 *   MAX_RMS  and a loud one would otherwise drive it past anything a human
 *            can produce. Above this, it counts, whatever the room is doing.
 */
const MIN_RMS = 0.015;
const MAX_RMS = 0.045;
const FLOOR_MULTIPLE = 4;

/** Full-scale for the meter. Singing into a phone lands well under 1.0. */
const LEVEL_FULL_RMS = 0.2;

/**
 * No verdict before the microphone has finished its entrance.
 *
 * A browser that has already been told "never" for this origin rejects
 * getUserMedia in the same frame it is asked, with no prompt — so without this
 * the mic pops up and vanishes inside one frame and the whole beat reads as a
 * rendering glitch. Slightly longer than the 560ms rise.
 */
const MIN_STAGE_MS = 700;

export function Sing({
  scale,
  bottomPx,
  onDone,
}: {
  /** The sprites' integer scale factor, so the mic matches their pixel size. */
  scale: number;
  /** Height of the text box — the mic rises from behind its top edge. */
  bottomPx: number;
  onDone: (outcome: SingOutcome) => void;
}) {
  const [risen, setRisen] = useState(false);
  const [listening, setListening] = useState(false);
  const meterRef = useRef<HTMLDivElement>(null);
  const clockRef = useRef<HTMLSpanElement>(null);
  /** Set once measurement is live; ends the listen early with what it has. */
  const stopEarly = useRef<(() => void) | null>(null);
  /** onDone must not be a dependency — a re-created callback would restart the mic. */
  const done = useRef(onDone);
  done.current = onDone;

  // Rise on the frame after mount, so the transition has an "off" state to
  // start from. Setting it during render would land both values in one paint
  // and the mic would simply appear.
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setRisen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const mountedAt = performance.now();
    let finished = false;
    let settling = false;
    let settleTimer = 0;
    let raf = 0;
    let stream: MediaStream | null = null;
    let source: MediaStreamAudioSourceNode | null = null;

    const teardown = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      try {
        source?.disconnect();
      } catch {
        /* already gone */
      }
      source = null;
      // The indicator in the browser chrome only goes out when every track
      // stops. Missing this is the bug people notice and do not report.
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
    };

    const settle = (outcome: SingOutcome) => {
      if (finished || settling) return;
      settling = true;
      // The microphone is released the instant the verdict is known, whatever
      // the delay below does — holding a live track open for the sake of an
      // animation is not a trade worth making.
      teardown();
      settleTimer = window.setTimeout(
        () => {
          if (finished) return;
          finished = true;
          done.current(outcome);
        },
        Math.max(0, MIN_STAGE_MS - (performance.now() - mountedAt)),
      );
    };

    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        settle('denied');
        return;
      }

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          // All three off on purpose. Noise suppression gates exactly the
          // half-hearted mumble this is trying to catch, and automatic gain
          // control amplifies an empty room until it reads as a performance —
          // between them they invert both branches.
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        });
      } catch {
        // Refused, dismissed, or no microphone on the device. The guards do
        // not distinguish; refusing to sing is its own answer.
        settle('denied');
        return;
      }

      if (finished) {
        teardown();
        return;
      }

      unlock();
      const ctx = audioContext();
      if (!ctx) {
        // Mic granted, but there is no WebAudio to measure it with. They were
        // given the chance and cannot be heard, which is the silent branch —
        // not a refusal.
        settle('silent');
        return;
      }
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch {
          /* measured anyway; a suspended context just reads zeroes */
        }
      }
      if (finished) {
        teardown();
        return;
      }

      source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      // No smoothing: a short sharp noise is a pass and smoothing averages it
      // away. The meter is smoothed separately, for the eye rather than the test.
      analyser.smoothingTimeConstant = 0;
      source.connect(analyser);
      // Deliberately NOT connected to ctx.destination. That is a feedback loop,
      // and on a laptop speaker it is an instant howl.

      const buf = new Float32Array(analyser.fftSize);
      const started = performance.now();
      let last = started;
      let floor = Number.POSITIVE_INFINITY;
      let noiseMs = 0;
      let shown = 0;

      setListening(true);

      const tick = () => {
        if (finished) return;
        const now = performance.now();
        const dt = now - last;
        last = now;
        const elapsed = now - started;

        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i += 1) {
          const v = buf[i] ?? 0;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);

        // The floor tracks every frame, warmup included — the quietest moment
        // of the whole listen is the best estimate of the room, and it is
        // usually before they start rather than after.
        if (rms < floor) floor = rms;
        if (elapsed >= WARMUP_MS) {
          const threshold = Math.min(MAX_RMS, Math.max(MIN_RMS, floor * FLOOR_MULTIPLE));
          if (rms > threshold) noiseMs += dt;
        }

        // Written straight to the DOM rather than through state: this runs at
        // frame rate, and re-rendering the scene sixty times a second to move
        // a bar is how you make a login screen drop frames.
        const target = Math.min(1, Math.sqrt(rms / LEVEL_FULL_RMS));
        shown += (target - shown) * 0.3;
        if (meterRef.current) meterRef.current.style.transform = `scaleX(${shown.toFixed(3)})`;
        if (clockRef.current) {
          const left = Math.max(0, WARMUP_MS + LISTEN_MS - elapsed);
          clockRef.current.textContent = (left / 1000).toFixed(1);
        }

        if (elapsed >= WARMUP_MS + LISTEN_MS) {
          settle(noiseMs >= NOISE_MS ? 'sang' : 'silent');
          return;
        }
        raf = requestAnimationFrame(tick);
      };

      stopEarly.current = () => settle(noiseMs >= NOISE_MS ? 'sang' : 'silent');
      raf = requestAnimationFrame(tick);
    })();

    return () => {
      finished = true;
      clearTimeout(settleTimer);
      stopEarly.current = null;
      teardown();
    };
  }, []);

  const micW = MIC_W * scale;
  const micH = MIC_H * scale;

  return (
    <>
      {/* The mic itself. z-30 puts it *under* the text box, which is what lets
          it start fully hidden behind the box's top edge and rise into view
          between the two bouncers rather than over the dialogue. */}
      <img
        src={MIC_SRC}
        alt=""
        aria-hidden
        draggable={false}
        className="sprite pointer-events-none fixed left-1/2 z-30 select-none transition-transform duration-[560ms] ease-out"
        style={{
          width: micW,
          height: micH,
          bottom: bottomPx,
          transform: risen ? 'translate(-50%, 18%)' : 'translate(-50%, 100%)',
        }}
      />

      {/* Level meter and countdown, sitting on the box's top edge at the foot
          of the stand. Only once measurement is actually running — a meter
          that appears while the permission prompt is still up reads as if the
          page were already listening. */}
      {listening && (
        <div
          className="pointer-events-none fixed inset-x-0 z-[45] flex items-center gap-3 px-5"
          style={{ bottom: bottomPx + 10 }}
        >
          <div className="h-2 flex-1 overflow-hidden rounded-sm border border-paper/25 bg-ink/70">
            <div
              ref={meterRef}
              className="h-full origin-left bg-bulb"
              style={{ transform: 'scaleX(0)' }}
            />
          </div>
          <span className="font-mono text-xs text-bulb/70">
            <span ref={clockRef}>{(LISTEN_MS / 1000).toFixed(1)}</span>s
          </span>
        </div>
      )}

      {/* Same corner as the bribe's way out, for the same reason: every beat
          that holds the scene has to have a visible end. */}
      {listening && (
        <button
          type="button"
          onClick={() => stopEarly.current?.()}
          className="slab fixed bottom-6 right-5 z-50"
        >
          Gotovo
        </button>
      )}
    </>
  );
}
